// Route↔registry response tie (ADR 027 / BL-855).
//
// `respond(entry, verb, data)` binds a route's response body to the DTO the
// registry declares for that (entry, verb) via `responseType<DTO>()`. The route
// physically cannot send a shape the client isn't typed for — server-send and
// client-infer both derive from the ONE registry declaration, so the DB→frontend
// type chain is end-to-end (tsc-enforced at every link) rather than "two
// segments that happen to name the same type by convention".
//
// Server-only (imports `next/server`). The registry entry it takes is
// client-safe; only this wrapper is server-bound.
//
// BL-1321: lifted from `apps/admin-web/src/lib/api/respond.ts` when bidder-web
// needed the same tie. Copying it would have been the cross-app duplication
// `sharing.md` § 1 exists to stop — two copies of a type-checking helper drift
// silently, and the drift shows up as a route sending a shape its client isn't
// typed for, which is the exact failure this helper prevents. Admin keeps a
// re-export so its ~50 call sites are untouched. `parse-body.ts` beside it also
// imports `next/server`.

import { NextResponse } from 'next/server';
import { okEnvelope } from './envelopes';
import type { HttpVerb, InferResponse, RouteEntry, VerbSpec } from './registry';

/**
 * Return the standard success envelope for a route, type-checked against the DTO
 * the registry declares for `(entry, verb)`.
 *
 * @param entry - The registry route entry (e.g. `lots.id.index`). Carries the
 *   declared response type; not read at runtime.
 * @param verb - The HTTP verb whose `responseType<DTO>()` marker types `data`.
 * @param data - The response payload. Must satisfy `InferResponse<entry[verb]>`;
 *   a mismatch is a compile error — this is the tie.
 * @param init - Optional `ResponseInit` (status, headers) plus an optional
 *   `warning` — the envelope's partial-failure notice (`okEnvelope`'s second
 *   arg; error-handling-chain.md "Partial-failure responses"). BL-1096.
 * @returns `NextResponse.json({ success: true, data })` (plus `warning` when provided).
 * @sideEffect none — pure construction of a `NextResponse`.
 */
export function respond<
  M extends Partial<Record<HttpVerb, VerbSpec>>,
  V extends keyof M & HttpVerb,
>(
  entry: RouteEntry<Record<string, string>, M>,
  verb: V,
  data: InferResponse<M[V]>,
  init?: ResponseInit & { warning?: string },
): NextResponse {
  // `entry`/`verb` are type carriers — they pin `M`/`V` so `data` is checked
  // against the registry's declared DTO. Neither is read at runtime.
  void entry;
  void verb;
  const { warning, ...responseInit } = init ?? {};
  return NextResponse.json(okEnvelope(data, warning), responseInit);
}
